import React, { useState, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  Chip,
  InputAdornment,
  CircularProgress,
  Alert,
  List,
  ListItem,
  ListItemButton,
  Divider,
  Pagination,
} from '@mui/material';
import {
  Search,
  Inventory,
  CalendarToday,
  Inventory2,
} from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { WarehouseAPI } from '../services/api';
import { useSearchDebounce } from '../hooks/useDebounce';
import { BundleSearchResult } from '../types';

interface BundleSelectorProps {
  open: boolean;
  onClose: () => void;
  onBundleSelect: (bundle: BundleSearchResult) => void;
}

const BundleSelector: React.FC<BundleSelectorProps> = ({
  open,
  onClose,
  onBundleSelect,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(0);
  const limit = 10;

  // Use custom debounce hook with 400ms delay
  const { debouncedSearch, isDebouncing } = useSearchDebounce(searchTerm, 400);

  // Reset to first page when search term changes
  React.useEffect(() => {
    setCurrentPage(0);
  }, [debouncedSearch]);

  // Fetch bundles based on search term
  const { 
    data: bundleData, 
    isLoading, 
    error
  } = useQuery({
    queryKey: ['bundle-search', debouncedSearch, currentPage],
    queryFn: () => WarehouseAPI.searchBundles(debouncedSearch || 'heart', currentPage, limit),
    enabled: open && (debouncedSearch.length >= 2 || debouncedSearch === ''),
    staleTime: 30000, // Cache for 30 seconds
  });

  const handleSearch = useCallback((value: string) => {
    setSearchTerm(value);
  }, []);

  const handlePageChange = (_: React.ChangeEvent<unknown>, page: number) => {
    setCurrentPage(page - 1); // MUI Pagination is 1-indexed, API is 0-indexed
  };

  const handleBundleSelect = (bundle: BundleSearchResult) => {
    onBundleSelect(bundle);
    onClose();
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const totalPages = bundleData ? Math.ceil(bundleData.total / limit) : 0;

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { minHeight: '600px', maxHeight: '80vh' }
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Inventory />
          Select Bundle
        </Box>
      </DialogTitle>
      
      <DialogContent>
        <Box sx={{ mb: 3 }}>
          <TextField
            fullWidth
            placeholder="Search bundles by SKU (e.g., heart, gift, box)..."
            value={searchTerm}
            onChange={(e) => handleSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
              endAdornment: isDebouncing && searchTerm.length >= 2 && (
                <InputAdornment position="end">
                  <CircularProgress size={20} />
                </InputAdornment>
              ),
            }}
            helperText={
              isDebouncing && searchTerm.length >= 2
                ? "Searching as you type..."
                : bundleData && debouncedSearch.length >= 2
                ? `${bundleData.total} bundles found`
                : "Enter at least 2 characters to search"
            }
            sx={{ mb: 2 }}
          />
          
          {/* Show debouncing indicator while user is typing */}
          {isDebouncing && searchTerm.length >= 2 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress size={20} />
              <Typography sx={{ ml: 2 }} variant="body2" color="text.secondary">
                Typing...
              </Typography>
            </Box>
          )}

          {debouncedSearch && debouncedSearch.length < 2 && !isDebouncing && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Please enter at least 2 characters to search
            </Alert>
          )}
        </Box>

        {/* Show loading indicator while API call is in progress */}
        {isLoading && !isDebouncing && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
            <Typography sx={{ ml: 2 }}>Searching bundles...</Typography>
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            Failed to load bundles. Please try again.
          </Alert>
        )}

        {bundleData && bundleData.data.length === 0 && !isLoading && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography color="text.secondary">
              No bundles found for "{debouncedSearch}"
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Try searching with different keywords
            </Typography>
          </Box>
        )}

        {bundleData && bundleData.data.length > 0 && (
          <>
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Found {bundleData.total} bundles
                {debouncedSearch && ` matching "${debouncedSearch}"`}
              </Typography>
            </Box>

            <List sx={{ maxHeight: '400px', overflow: 'auto' }}>
              {bundleData.data.map((bundle, index) => (
                <React.Fragment key={bundle.id}>
                  <ListItem disablePadding>
                    <ListItemButton
                      onClick={() => handleBundleSelect(bundle)}
                      sx={{
                        borderRadius: 1,
                        mb: 1,
                        border: '1px solid',
                        borderColor: 'divider',
                        '&:hover': {
                          borderColor: 'primary.main',
                          backgroundColor: 'action.hover',
                        },
                      }}
                    >
                      <Box sx={{ width: '100%' }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 1 }}>
                          <Typography variant="h6" component="div">
                            {bundle.bundleSku}
                          </Typography>
                          <Chip
                            size="small"
                            icon={<Inventory2 />}
                            label={`${bundle.products.length} Products`}
                            color="primary"
                            variant="outlined"
                          />
                        </Box>
                        
                        {bundle.name && (
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            {bundle.name}
                          </Typography>
                        )}
                        
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
                          {bundle.products.slice(0, 5).map((product) => (
                            <Chip
                              key={product}
                              label={product}
                              size="small"
                              variant="outlined"
                            />
                          ))}
                          {bundle.products.length > 5 && (
                            <Chip
                              label={`+${bundle.products.length - 5} more`}
                              size="small"
                              variant="outlined"
                              color="secondary"
                            />
                          )}
                        </Box>
                        
                        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <CalendarToday fontSize="small" color="disabled" />
                            <Typography variant="caption" color="text.secondary">
                              Created: {formatDate(bundle.createdAt)}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Typography variant="caption" color="text.secondary">
                              Updated: {formatDate(bundle.updatedAt)}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    </ListItemButton>
                  </ListItem>
                  {index < bundleData.data.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>

            {totalPages > 1 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                <Pagination
                  count={totalPages}
                  page={currentPage + 1}
                  onChange={handlePageChange}
                  color="primary"
                  showFirstButton
                  showLastButton
                />
              </Box>
            )}
          </>
        )}
      </DialogContent>
      
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
      </DialogActions>
    </Dialog>
  );
};

export default BundleSelector;